import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

function Avatar({ name, size = 40 }) {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'var(--gold-dim)', border: '1.5px solid var(--gold)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--gold)', fontFamily: 'DM Mono, monospace',
      fontSize: size * 0.32, fontWeight: 600, flexShrink: 0
    }}>
      {initials}
    </div>
  )
}

export default function Directorio() {
  const { usuario } = useAuth()
  const [personas, setPersonas] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [page, setPage] = useState(0)
  const PER_PAGE = 30

  useEffect(() => { fetchPersonas() }, [search, page])

  const fetchPersonas = async () => {
    setLoading(true)
    let q = supabase.from('personas').select('*', { count: 'exact' }).order('apellido').range(page * PER_PAGE, (page + 1) * PER_PAGE - 1)
    if (search) q = q.or(`nombre.ilike.%${search}%,apellido.ilike.%${search}%,dni.ilike.%${search}%`)
    const { data } = await q
    setPersonas(data || [])
    setLoading(false)
  }

  const handleEdit = (persona) => {
    setEditingId(persona.id)
    setEditForm({ nombre: persona.nombre, apellido: persona.apellido, dni: persona.dni, telefono: persona.telefono || '' })
  }

  const handleSave = async () => {
    await supabase.from('personas').update(editForm).eq('id', editingId)
    setEditingId(null)
    fetchPersonas()
  }

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta persona del directorio?')) return
    await supabase.from('personas').delete().eq('id', id)
    fetchPersonas()
  }

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 36, color: 'var(--text)', marginBottom: 4 }}>Directorio</h1>
        <p style={{ color: 'var(--text3)', fontSize: 14 }}>Personas registradas en el sistema</p>
      </div>

      <input
        className="input"
        placeholder="Buscar por nombre, apellido o DNI..."
        value={search}
        onChange={e => { setSearch(e.target.value); setPage(0) }}
        style={{ marginBottom: 20, maxWidth: 480 }}
      />

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text3)' }}>Cargando...</div>
        ) : personas.length === 0 ? (
          <div style={{ padding: '64px 0', textAlign: 'center', color: 'var(--text3)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
            <div>No se encontraron personas</div>
          </div>
        ) : personas.map((persona, i) => (
          <div key={persona.id} style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
            borderBottom: i < personas.length - 1 ? '1px solid var(--border)' : 'none',
          }}>
            <Avatar name={`${persona.nombre} ${persona.apellido}`} size={40} />

            {editingId === persona.id ? (
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 140px 160px', gap: 8 }}>
                <input className="input" value={editForm.nombre} onChange={e => setEditForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre" style={{ padding: '8px 12px', fontSize: 13 }} />
                <input className="input" value={editForm.apellido} onChange={e => setEditForm(f => ({ ...f, apellido: e.target.value }))} placeholder="Apellido" style={{ padding: '8px 12px', fontSize: 13 }} />
                <input className="input" value={editForm.dni} onChange={e => setEditForm(f => ({ ...f, dni: e.target.value }))} placeholder="DNI" style={{ padding: '8px 12px', fontSize: 13 }} />
                <input className="input" value={editForm.telefono} onChange={e => setEditForm(f => ({ ...f, telefono: e.target.value }))} placeholder="Teléfono" style={{ padding: '8px 12px', fontSize: 13 }} />
              </div>
            ) : (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{persona.nombre} {persona.apellido}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                  DNI {persona.dni}{persona.telefono && ` · ${persona.telefono}`}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {editingId === persona.id ? (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>Cancelar</button>
                  <button className="btn btn-gold btn-sm" onClick={handleSave}>Guardar</button>
                </>
              ) : (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(persona)}>Editar</button>
                  {usuario?.rol === 'admin' && (
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(persona.id)}>✕</button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Paginación */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>← Anterior</button>
        <span style={{ padding: '8px 16px', fontSize: 13, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>Página {page + 1}</span>
        <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => p + 1)} disabled={personas.length < PER_PAGE}>Siguiente →</button>
      </div>
    </div>
  )
}
